import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Monitor, Smartphone, KeyRound, Copy, RefreshCw, Trash2, Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { devicesAPI, locationsAPI } from '../lib/api';

const DEVICE_TYPES = [
  { value: 'tv', label: 'TV-Anzeige', icon: Monitor },
  { value: 'terminal', label: 'Zeiterfassungs-Terminal', icon: Smartphone },
];

function formatRelative(iso) {
  if (!iso) return 'nie';
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  return then.toLocaleDateString('de-DE');
}

function buildPairUrl(device, token) {
  const base = window.location.origin;
  if (device.type === 'tv') {
    return `${base}/einsatzplan/tv/${device.location_id}?pair=${token}`;
  }
  return `${base}/zeiterfassung/terminal/${device.location_id}?pair=${token}`;
}

export default function Devices({ embedded = false }) {
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [pairUrl, setPairUrl] = useState(null);
  const [pairDeviceName, setPairDeviceName] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([devicesAPI.list(), locationsAPI.getAll()]);
      setDevices(d.data);
      setLocations(l.data);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const showPair = (device, token) => {
    setPairDeviceName(device.name);
    setPairUrl(buildPairUrl(device, token));
  };

  const handleCreate = async (form) => {
    try {
      const res = await devicesAPI.create(form);
      toast.success('Gerät angelegt');
      setCreateOpen(false);
      showPair(res.data, res.data.pair_token);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleRotate = async (device) => {
    try {
      const res = await devicesAPI.rotate(device.id);
      toast.success('Neues Token erzeugt');
      showPair(res.data, res.data.pair_token);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleToggleActive = async (device) => {
    try {
      await devicesAPI.update(device.id, { is_active: !device.is_active });
      toast.success(device.is_active ? 'Gerät deaktiviert' : 'Gerät aktiviert');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleDelete = async (device) => {
    try {
      await devicesAPI.delete(device.id);
      toast.success('Gerät gelöscht');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6" data-testid="devices-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6" /> TV-/Terminal-Geräte
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Pair-Tokens für öffentliche Anzeigegeräte verwalten.
              Jedes Gerät wird einmalig mit der Pair-URL aktiviert; ohne Token funktioniert die TV-/Terminal-URL nicht mehr.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="devices-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Neues Gerät
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Geräte ({devices.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Lade …</p>
            ) : devices.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                Noch keine Geräte angelegt. Klicke „Neues Gerät", um ein TV oder Terminal zu pairen.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase text-muted-foreground border-b">
                    <tr>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Typ</th>
                      <th className="py-2 pr-4">Standort</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Letzte Aktivität</th>
                      <th className="py-2 pr-4">Angelegt von</th>
                      <th className="py-2">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => {
                      const T = DEVICE_TYPES.find((t) => t.value === d.type) || DEVICE_TYPES[0];
                      const Icon = T.icon;
                      return (
                        <tr key={d.id} className="border-b last:border-0" data-testid={`device-row-${d.id}`}>
                          <td className="py-2 pr-4 font-medium">{d.name}</td>
                          <td className="py-2 pr-4 text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {T.label}</span>
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground">{d.location_name || '—'}</td>
                          <td className="py-2 pr-4">
                            {d.is_active ? (
                              <Badge variant="default" className="bg-emerald-600">aktiv</Badge>
                            ) : (
                              <Badge variant="secondary">deaktiviert</Badge>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground">{formatRelative(d.last_seen_at)}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{d.created_by_name || '—'}</td>
                          <td className="py-2">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline" size="sm"
                                onClick={() => handleRotate(d)}
                                title="Neues Token erzeugen (altes wird ungültig)"
                                data-testid={`device-rotate-${d.id}`}
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="outline" size="sm"
                                onClick={() => handleToggleActive(d)}
                                title={d.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                data-testid={`device-toggle-${d.id}`}
                              >
                                <KeyRound className="h-3.5 w-3.5" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="text-red-600" data-testid={`device-delete-${d.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Gerät „{d.name}" wirklich löschen?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Das Token wird damit endgültig ungültig. Das Gerät zeigt danach den Sperrbildschirm.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDelete(d)}>Löschen</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateDeviceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        locations={locations}
        onSubmit={handleCreate}
      />

      <PairUrlDialog
        url={pairUrl}
        deviceName={pairDeviceName}
        onClose={() => setPairUrl(null)}
      />
    </MaybeLayout>
  );
}

function CreateDeviceDialog({ open, onOpenChange, locations, onSubmit }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('tv');
  const [locationId, setLocationId] = useState('');

  useEffect(() => {
    if (open) { setName(''); setType('tv'); setLocationId(''); }
  }, [open]);

  const canSubmit = name.trim() && type && locationId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Neues Gerät anlegen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="dev-name">Bezeichnung</Label>
            <Input
              id="dev-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="z. B. TV Empfangshalle Nord"
              data-testid="device-create-name"
            />
          </div>
          <div>
            <Label>Typ</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="device-create-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEVICE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Standort</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger data-testid="device-create-location"><SelectValue placeholder="Standort wählen" /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            disabled={!canSubmit}
            onClick={() => onSubmit({ name: name.trim(), type, location_id: locationId })}
            data-testid="device-create-submit"
          >
            Erstellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PairUrlDialog({ url, deviceName, onClose }) {
  if (!url) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Pair-URL für „{deviceName}"</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-200">
            <strong>Wichtig:</strong> Diese URL wird <em>nur einmal</em> angezeigt.
            Öffne sie am Anzeige-Gerät (Fire-TV / Tablet) — das Gerät übernimmt das Token automatisch und
            entfernt es danach aus der Adressleiste.
          </div>
          <div className="flex gap-2">
            <Input
              readOnly value={url} className="font-mono text-xs"
              onFocus={(e) => e.target.select()}
              data-testid="device-pair-url"
            />
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(url);
                toast.success('URL kopiert');
              }}
              data-testid="device-pair-copy"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Verstanden</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
